import { EraQL, Expression, TC, Ty } from '@cotera/era';
import { DG } from '@cotera/sdk/core';
import _ from 'lodash';
import type * as monaco from 'monaco-editor';
import type { Monaco } from '@monaco-editor/react';

const makeRelSuggestions = (monacoInstance: Monaco, range: monaco.Range) => {
  return Object.entries(EraQL.DOCS.REL_SPECIAL_FORMS_DOCS).map(
    ([key, value]) => {
      return {
        label: key,
        kind: monacoInstance.languages.CompletionItemKind.Function,
        insertText: `${key}()`,
        range: range,
        documentation: value.description,
        detail: value.examples.map(([e, r]) => `${e} -> ${r}`).join('\n'),
      };
    }
  );
};

const makeExprSuggestions = (
  monacoInstance: Monaco,
  range: monaco.Range,
  ty: Ty.ExtendedAttributeType
) => {
  return Object.entries(EraQL.DOCS.BUILTTINS_DOCS)
    .filter(([_, value]) => {
      return TC.implementsTypeConstraint(ty, value.typeConstraints).isOk();
    })
    .map(([key, value]) => {
      return {
        label: key,
        kind: monacoInstance.languages.CompletionItemKind.Function,
        insertText: `${key}()`,
        detail: value.description,
        range: range,
        documentation: value.examples
          .map(([e, r]) => `${e} -> ${r}`)
          .join('\n'),
      };
    });
};

export const FunctionCompletionProvider = (props: {
  isTemplated: boolean;
  type: 'expr' | 'rel';
  parser: DG.Graph.RelativeParser;
  monacoInstance: Monaco;
}): monaco.languages.CompletionItemProvider => ({
  triggerCharacters: [' '],
  provideCompletionItems(model, position) {
    // Check if the cursor is inside a '{{ }}' block
    const nearestContent = props.isTemplated
      ? getNearestDoubleBraceContent(props.monacoInstance, model, position)
      : model.getValue();

    if (!nearestContent) {
      return { suggestions: [] };
    }

    const lineContent = model.getLineContent(position.lineNumber);
    const textUntilPosition = lineContent.substring(0, position.column - 1);
    if (!textUntilPosition.endsWith('|> ')) {
      return { suggestions: [] };
    }

    const wordUntil = model.getWordUntilPosition(position);
    const range = new props.monacoInstance.Range(
      position.lineNumber,
      wordUntil.startColumn,
      position.lineNumber,
      wordUntil.endColumn
    );

    // Get the text before the cursor position
    const textBeforeCursor = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });

    // Remove the trailing '|> ' if it exists
    const contextBeforePipe = textBeforeCursor.endsWith('|> ')
      ? textBeforeCursor.slice(0, -3)
      : textBeforeCursor;

    //get text after the cursor minus the '|> '
    // Get text after the cursor position
    const textAfterCursor = model.getValueInRange({
      startLineNumber: position.lineNumber,
      startColumn: position.column,
      endLineNumber: model.getLineCount(),
      endColumn: model.getLineMaxColumn(model.getLineCount()),
    });

    // This represents all text after the cursor, without the '|> '
    const contextAfterPipe = textAfterCursor.startsWith('|> ')
      ? textAfterCursor.slice(3)
      : textAfterCursor;

    // We'll use this for parsing
    const contextBetweenPipe = contextBeforePipe + contextAfterPipe;

    const parseRes =
      props.type === 'expr'
        ? props.parser.parseExpr(contextBetweenPipe)
        : props.parser.parseRel(contextBetweenPipe);

    if (parseRes.isErr()) {
      return { suggestions: [] };
    }

    if (props.type === 'expr') {
      const expr = parseRes.value as Expression;
      const suggestions = makeExprSuggestions(
        props.monacoInstance,
        range,
        expr.ty
      );

      return { suggestions };
    }

    const startofExpr = textBeforeCursor.lastIndexOf(':');
    const endofExpr = textBeforeCursor.indexOf('|>', startofExpr);

    const expr = textBeforeCursor.slice(startofExpr + 1, endofExpr);

    const exprRes = props.parser.parseExpr(expr);

    if (exprRes.isOk()) {
      const suggestions = makeExprSuggestions(
        props.monacoInstance,
        range,
        exprRes.value.ty
      );

      return { suggestions };
    }

    const suggestions = makeRelSuggestions(props.monacoInstance, range);

    return { suggestions };
  },
});

export const ColumnCompletionProvider = (props: {
  isTemplated: boolean;
  parser: DG.Graph.RelativeParser;
  monacoInstance: Monaco;
  graph?: DG.BuildExplorer;
}): monaco.languages.CompletionItemProvider => ({
  triggerCharacters: ['"'],
  provideCompletionItems(model, position) {
    // Check if the cursor is inside a '{{ }}' block
    if (props.isTemplated) {
      if (!isInsideDoubleBrace(props.monacoInstance, model, position)) {
        return { suggestions: [] };
      }
    }

    const lineContent = model.getLineContent(position.lineNumber);
    if (lineContent[position.column - 2] !== '"') {
      return { suggestions: [] };
    }

    const wordUntil = model.getWordUntilPosition(position);
    const range = new props.monacoInstance.Range(
      position.lineNumber,
      wordUntil.startColumn,
      position.lineNumber,
      wordUntil.endColumn
    );

    // Get attributes from parser (existing behavior)
    const parserAttributes = props.parser.availableAttributes();

    // Extract parent columns if graph is provided
    const parentAttributes: string[] = [];
    if (props.graph !== undefined) {
      const fullText = model.getValue();
      const parentRefs = DG.extractSymbolReferencesFromEraql(fullText);

      for (const parentName of parentRefs) {
        const parentResult = props.graph.graph[parentName];
        if (
          parentResult !== undefined &&
          parentResult.isOk() &&
          parentResult.value.item.t === 'rel'
        ) {
          const attrs = Object.keys(parentResult.value.item.rel.attributes);
          parentAttributes.push(...attrs);
        }
      }
    }

    // Combine and deduplicate
    const allAttributes = [
      ...new Set([...parserAttributes, ...parentAttributes]),
    ];

    const suggestions = _.uniqBy(
      allAttributes.map((attr) => ({
        label: attr,
        kind: props.monacoInstance.languages.CompletionItemKind.Field,
        insertText: attr,
        range: range,
      })),
      'label'
    );

    return { suggestions };
  },
});

function isInsideDoubleBrace(
  monacoInstance: Monaco,
  model: monaco.editor.ITextModel,
  position: monaco.Position
) {
  const textBefore = model.getValueInRange(
    new monacoInstance.Range(1, 1, position.lineNumber, position.column)
  );
  const lastOpen = textBefore.lastIndexOf('{{');
  const lastClose = textBefore.lastIndexOf('}}');
  // Return true if last '{{' appears after the last '}}'
  return lastOpen > lastClose;
}

// A helper to find the nearest '{{ }}' block content relative to the current position
function getNearestDoubleBraceContent(
  monacoInstance: Monaco,
  model: monaco.editor.ITextModel,
  position: monaco.Position
) {
  const fullText = model.getValue();
  const offset = model.getOffsetAt(position);

  // Find the last occurrence of '{{' before the cursor
  const openIndex = fullText.lastIndexOf('{{', offset);
  if (openIndex === -1) {
    return null;
  } // No opening found

  // Find the corresponding closing '}}' after openIndex
  const closeIndex = fullText.indexOf('}}', openIndex);

  // If no closing '}}' found before the cursor, assume we're inside an open block
  // If closeIndex exists and is before the cursor, we're outside current block
  if (closeIndex !== -1 && closeIndex < offset) {
    return null;
  }

  // Extract content between '{{' and the current cursor or the next '}}'
  const blockStart = openIndex + 2; // after '{{'
  const blockEnd =
    closeIndex === -1 || closeIndex > offset ? offset : closeIndex;
  const blockContent = fullText.slice(blockStart, blockEnd);
  return blockContent;
}
